import capitalize from 'lodash/capitalize';
import Head from 'next/head';
import type { NextSeoProps } from 'next-seo/pages';
import { generateNextSeo } from 'next-seo/pages';
import { parse } from 'querystring';

import {
  addTrailingSlashUnlessFile,
  extractPathFromUrl,
  removeTrailingSlash,
} from '@/utils/urlUtils';

import type {
  ContentfulAsset,
  ContentfulSeoConfiguration,
} from '@/graphql/contentful/api';
import {
  getBrandConfig,
  LocaleConfiguration,
} from '@/lib/configuration/configuration';
import { BRAND } from '@/lib/const';
import { paths } from '@/lib/paths';
import { getRegionFromLocale } from '@/lib/regions';
import { SEO_HREFLANG_COUNTRIES } from '@/lib/seo/Config';
import { makeHrefLangs } from '@/lib/seo/Functions';
import { SEO_DEFAULTS } from '@/lib/seo/SeoVariables';

export interface SEOHeadProps {
  requestUrl: string | null;
  seoConfig?: Partial<ContentfulSeoConfiguration> & { seoHreflangs?: string[] };
}

export function cleanStoresPageParam(urlString: string): string {
  if (!urlString?.includes('/stores/breitling-boutiques/')) {
    return urlString;
  }

  try {
    const urlObj = new URL(urlString);

    const pageVal = urlObj.searchParams.get('page');
    if (pageVal) {
      const cleaned = pageVal.replace(/\/+/g, '');
      urlObj.searchParams.set('page', cleaned);
    }

    return urlObj.toString();
  } catch (err) {
    return urlString;
  }
}

function removeDuplicateLocales(urlPath: string, locale: string): string {
  const regex = new RegExp(`/${locale}/{1}`, 'g');
  const uniquePath = urlPath.replace(regex, `/${locale}/`); // Ensure a single occurrence of the locale
  return uniquePath.replace(new RegExp(`(/${locale})+`, 'g'), `/${locale}`);
}

const makeLanguageAlternates = (
  alternateLanguages: string[],
  locale: string,
  path: string,
  query: string,
) =>
  alternateLanguages.map((altLanguage) => {
    const pathWithoutLocale = path.replace(`/${locale}/`, '');
    const alternateLocale = altLanguage
      .toLowerCase()
      .split('-')
      .reverse()
      .join('-');
    let href = `${process.env.NEXT_PUBLIC_URL}${alternateLocale}/${pathWithoutLocale}${query}`;
    href = addTrailingSlashUnlessFile(href);

    return {
      href,
      hrefLang: altLanguage,
    };
  });

const CANONICAL_QUERY_PARAMS = ['page'];

const filterQueryParameters = (
  queryString: string,
  allowedQueryParams: string[],
) => {
  if (!queryString) {
    return '';
  }

  const inputParams = new URLSearchParams(queryString);
  const outputParams = new URLSearchParams();

  // Copy only allowed params
  Array.from(inputParams.entries()).forEach(([key, value]) => {
    if (allowedQueryParams.includes(key)) {
      outputParams.append(key, value);
    }
  });

  // Sanitize the page parameter: keep leading digits, normalize to integer, drop if <= 1 or invalid
  if (outputParams.has('page')) {
    const rawPage = outputParams.get('page') ?? '';
    const match = String(rawPage).match(/^\d+/);
    const pageNumber = match ? parseInt(match[0], 10) : 0;
    if (!pageNumber || pageNumber <= 1) {
      outputParams.delete('page');
    } else {
      outputParams.set('page', String(pageNumber));
    }
  }

  const query = outputParams.toString();
  const finalQueryParams = query ? `?${query}` : '';

  return removeTrailingSlash(finalQueryParams);
};

// List of page paths that should exclude the title suffix
const EXCLUDED_PAGE_PATHS = [
  (countrySlug: string) =>
    paths.stores.breitlingBoutiques.asPath({ country: countrySlug }),
];

function isExcludedPage(pathname: string, countrySlug: string): boolean {
  return EXCLUDED_PAGE_PATHS.some((pathFn) =>
    pathname.endsWith(pathFn(countrySlug)),
  );
}

const TITLE_SUFFIX = `| ${capitalize(BRAND)}`;

// Function to append suffix to title if needed
export function appendTitleSuffix(
  title: string,
  pathname: string,
  countrySlug: string,
): string {
  if (title.trim() === '') return title;
  if (title?.includes(TITLE_SUFFIX)) return title;
  if (isExcludedPage(pathname, countrySlug)) return title;

  return `${title} ${TITLE_SUFFIX}`;
}

function makeNextSeoProps(
  seoTitle: string | null | undefined,
  seoDescription: string | null | undefined,
  seoIndex: boolean | null | undefined,
  seoCanonical: string | null | undefined,
  seoThumbnail: ContentfulAsset | null | undefined,
  requestUrl: string,
  seoHreflangs?: string[],
): NextSeoProps {
  const nextSeoProps: NextSeoProps = {};
  nextSeoProps.title = seoTitle || SEO_DEFAULTS.EN.title;
  nextSeoProps.description = seoDescription || SEO_DEFAULTS.EN.description;
  // Note: adding robots tag via additionalMetaTags with explicit key for better deduplication
  const url = new URL(requestUrl);

  const pathSegments = url.pathname.split('/').filter(Boolean);
  const locale = pathSegments[0];
  const cleanPath = removeDuplicateLocales(url.pathname, locale);
  const finalQueryParams = filterQueryParameters(
    url.search,
    CANONICAL_QUERY_PARAMS,
  );

  const currentUrl = `${addTrailingSlashUnlessFile(
    `${process.env.NEXT_PUBLIC_URL}${url.pathname.substring(1)}`,
  )}${finalQueryParams}`;

  switch (seoCanonical) {
    case 'watch':
    case 'strap':
      nextSeoProps.canonical = addTrailingSlashUnlessFile(
        `${process.env.NEXT_PUBLIC_URL}${cleanPath.substring(1)}`,
      );
      break;
    default:
      nextSeoProps.canonical = seoCanonical
        ? addTrailingSlashUnlessFile(
            `${process.env.NEXT_PUBLIC_URL}/${locale}/${seoCanonical}`.replace(
              /([^:]\/)\/+/g,
              '$1',
            ),
          )
        : currentUrl;
      break;
  }

  const metadataTags = [];

  metadataTags.push({
    name: 'viewport',
    content: 'width=device-width, initial-scale=1',
    keyOverride: 'viewport',
  });

  // Add robots tag with explicit key to prevent duplication
  const robotsContent = seoIndex === false ? 'noindex,follow' : 'index,follow';
  metadataTags.push({
    name: 'robots',
    content: robotsContent,
    keyOverride: 'robots',
  });

  if (getBrandConfig().metaTags.length) {
    getBrandConfig().metaTags.forEach((tag) => {
      metadataTags.push({
        name: tag.name,
        content: tag.content,
        keyOverride: tag.name,
      });
    });
  }

  nextSeoProps.openGraph = {
    title: nextSeoProps.title,
    description: nextSeoProps.description,
    url: currentUrl,
  };

  const thumbnailUrl = seoThumbnail?.url;
  const fallbackThumbnailUrl =
    process.env.NEXT_PUBLIC_URL + 'images/breitling.svg';

  metadataTags.push({
    name: 'thumbnail',
    content: thumbnailUrl ?? fallbackThumbnailUrl,
    keyOverride: 'thumbnail',
  });
  nextSeoProps.openGraph = {
    ...nextSeoProps.openGraph,
    images: [{ url: thumbnailUrl ?? fallbackThumbnailUrl }],
  };

  nextSeoProps.additionalMetaTags = metadataTags;
  const alternateLanguages = seoHreflangs?.length
    ? (seoHreflangs as string[])
    : makeHrefLangs(
        LocaleConfiguration.getInstance().getSupportedCountries(),
        SEO_HREFLANG_COUNTRIES,
        locale?.split('-')[1],
      );

  nextSeoProps.additionalLinkTags = makeLanguageAlternates(
    alternateLanguages,
    locale,
    extractPathFromUrl(nextSeoProps.canonical),
    finalQueryParams,
  ).map(({ href, hrefLang }) => ({
    rel: 'alternate',
    href,
    hrefLang,
  }));

  const { country } = getRegionFromLocale(locale);

  nextSeoProps.title = appendTitleSuffix(
    nextSeoProps.title ?? '',
    url.pathname,
    country.slug,
  );
  return nextSeoProps;
}

// index pagination and main page according to seoIndex value
export function getPageSeoIndexValue({ requestUrl, seoConfig }: SEOHeadProps) {
  if (seoConfig?.seoIndex === false) {
    return false;
  }

  const params = requestUrl?.split('?')[1];
  const queryParams = parse(params || '');

  // Treat 'country' as non-influential for main-page detection
  const filteredKeys = Object.keys(queryParams).filter(
    (key) => key !== 'country',
  );
  const isMainPage = filteredKeys.length === 0;
  // Paginated only when 'page' is the only relevant parameter (excluding 'country')
  const isPaginatedPage =
    queryParams.page !== undefined && filteredKeys.length === 1;

  return isMainPage || isPaginatedPage;
}

const SEOHead: React.FC<SEOHeadProps> = ({ requestUrl, seoConfig }) => {
  if (!requestUrl) return null;

  const seoIndex = getPageSeoIndexValue({
    requestUrl,
    seoConfig,
  });

  if (!seoConfig) {
    const nextSeoProps = makeNextSeoProps(
      SEO_DEFAULTS.EN.title,
      SEO_DEFAULTS.EN.description,
      seoIndex,
      null,
      null,
      requestUrl,
    );

    return <Head>{generateNextSeo(nextSeoProps)}</Head>;
  }

  const { seoCanonical, seoDescription, seoThumbnail, seoTitle, seoHreflangs } =
    seoConfig;

  const nextSeoProps = makeNextSeoProps(
    seoTitle,
    seoDescription,
    seoIndex,
    seoCanonical,
    seoThumbnail,
    requestUrl,
    seoHreflangs,
  );

  if (nextSeoProps.canonical) {
    nextSeoProps.canonical = cleanStoresPageParam(nextSeoProps.canonical);
  }

  return <Head>{generateNextSeo(nextSeoProps)}</Head>;
};

export default SEOHead;
